import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import Breadcrumb from "@/components/Breadcrumb";
import { useState, useMemo, useEffect } from "react";
import { useGetUsersPermission } from "@/hooks/userHook";
import { useGetAllGroups } from "@/hooks/groupHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { DefaultSecondaryButton } from "@/styles/globalstyles";
import RoleManagementTable from "./RoleManagementTable";
import SaveUserModal from "./SaveUserModal";
import { useNavigate } from "react-router-dom";
import type { UserWithPermission } from "@/api/userService";
import { usePermissions } from "@/hooks/usePermissions";

const RoleManagement = () => {
  const navigate = useNavigate();
  const canEdit = usePermissions().canEdit("role");
  const [tabValue, setTabValue] = useState<number>(0);
  const [filterGroup, setFilterGroup] = useState<string>("");
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [openAddUserModal, setOpenAddUserModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserWithPermission | null>(
    null,
  );

  const {
    data: users,
    isLoading: usersIsLoading,
    isError: usersIsError,
  } = useGetUsersPermission();

  const {
    data: groups,
    isError: groupsIsError,
    isFetching: groupsIsFetching,
  } = useGetAllGroups();

  useEffect(() => {
    document.title = "Role Management | AMPA";
  }, []);

  const filteredUsers = useMemo(() => {
    if (!users) return [];

    let filtered = [...(users || [])].sort((a, b) => {
      const statusA = a.status?.toLowerCase() === "active";
      const statusB = b.status?.toLowerCase() === "active";

      if (statusA !== statusB) {
        return statusA ? -1 : 1;
      }

      const dateA = new Date(a.updated_at).getTime();
      const dateB = new Date(b.updated_at).getTime();

      if (isNaN(dateA)) return 1;
      if (isNaN(dateB)) return -1;

      return dateB - dateA;
    });

    if (filterGroup) {
      filtered = filtered.filter((user) =>
        user.user_groups?.includes(filterGroup),
      );
    }

    if (searchFilter) {
      const lowerSearch = searchFilter.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.full_name.toLowerCase().includes(lowerSearch) ||
          user.email.toLowerCase().includes(lowerSearch) ||
          user.user_id.toString().includes(lowerSearch),
      );
    }

    if (tabValue === 1) {
      filtered = filtered.filter(
        (user) => user.status?.toLocaleLowerCase() === "active",
      );
    } else if (tabValue === 2) {
      filtered = filtered.filter(
        (user) => user.status?.toLocaleLowerCase() === "inactive",
      );
    }

    return filtered;
  }, [users, tabValue, filterGroup, searchFilter]);

  useQueryHandler({
    isError: usersIsError || groupsIsError,
    errorMessage: "เกิดข้อผิดพลาดในการโหลดข้อมูล",
  });

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleInsert = () => {
    setSelectedUser(null);
    setOpenAddUserModal(true);
  };

  const handleEdit = (user: UserWithPermission | null) => () => {
    setSelectedUser(user);
    setOpenAddUserModal(true);
  };

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <Breadcrumb current="role" />
      <Box sx={{ mt: 1, width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ flexGrow: 1 }}>
            <TextField
              label="ค้นหา ชื่อ / Email / รหัสพนักงาน"
              variant="outlined"
              size="small"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              sx={{
                mt: 2,
                mb: 1,
                width: "30%",
                "& .MuiInputLabel-root": {
                  fontSize: "0.8rem",
                  color: "#666666",
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#cccccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "#999999",
                  },
                },
              }}
            />
            <FormControl
              size="small"
              sx={{ mt: 2, mb: 1, mx: 1, width: "30%" }}
            >
              <InputLabel
                id="role-filter-label"
                sx={{
                  fontSize: "0.8rem",
                  color: "#666666",
                  "&.Mui-focused": {
                    color: "primary.main",
                  },
                }}
              >
                บทบาท
              </InputLabel>
              <Select
                labelId="role-filter-label"
                value={filterGroup}
                onChange={(e) => setFilterGroup(e.target.value)}
                label="บทบาท"
              >
                {groupsIsFetching ? (
                  <MenuItem disabled>กำลังโหลด...</MenuItem>
                ) : (
                  [
                    <MenuItem key="all" value="">
                      <Typography>ทั้งหมด</Typography>
                    </MenuItem>,
                    ...(groups?.map((group) => (
                      <MenuItem key={group.id} value={group.display_name}>
                        <Typography>{group.display_name}</Typography>
                      </MenuItem>
                    )) || []),
                  ]
                )}
              </Select>
            </FormControl>
          </Box>
          <Box gap={1} display="flex">
            <Button
              size="small"
              variant="outlined"
              sx={{ ...DefaultSecondaryButton, mt: 2, mb: 1 }}
              onClick={() => navigate("/master-role/authorization")}
            >
              กำหนดสิทธิ์
            </Button>
            {/* <Button
              size="small"
              variant="outlined"
              sx={{ ...DefaultSecondaryButton, mt: 2, mb: 1 }}
              disabled={!canEdit}
            >
              <Upload />
            </Button> */}
            <Button
              size="small"
              variant="outlined"
              onClick={handleInsert}
              sx={{
                ...DefaultSecondaryButton,
                mt: 2,
                mb: 1,
                alignItems: "center",
              }}
              disabled={!canEdit}
            >
              <Add /> เพิ่มผู้ใช้งาน
            </Button>
          </Box>
        </Box>
        <Box>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={handleChangeTab}
              aria-label="basic tabs example"
            >
              <Tab label="ทั้งหมด" />
              <Tab label="Active" />
              <Tab label="Inactive" />
            </Tabs>
          </Box>
          <Box sx={{ p: 1, borderRadius: 1 }}>
            <RoleManagementTable
              users={filteredUsers}
              isLoading={usersIsLoading}
              handleEdit={handleEdit}
            />
          </Box>
        </Box>
      </Box>

      <SaveUserModal
        open={openAddUserModal}
        handleClose={() => setOpenAddUserModal(false)}
        selectedUser={selectedUser}
      />
    </Box>
  );
};

export default RoleManagement;
